import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../lib/api.ts";
import { fmtNum } from "../lib/csv.ts";
import { theme } from "../theme.ts";
import type { TieOutResponse } from "@march/shared";

/** A nightly check older than this is called out as stale. */
const STALE_MS = 36 * 3600_000;

function ago(ms: number): string {
  const h = Math.round((Date.now() - ms) / 3600_000);
  if (h < 1) return "just now";
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

function monthLabel(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-US", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * "Matches NetSuite" badge. Every night the API asks NetSuite, independently,
 * how many units of each SKU sold on revenue lines last month and this month —
 * the Sales by Item figure — and compares it with the app's sold-alone numbers.
 * Click for what was checked and, when something differs, which SKUs.
 */
export function TieOutBadge() {
  const q = useQuery<TieOutResponse>({
    queryKey: ["tieout"],
    queryFn: () => apiGet<TieOutResponse>("/api/tieout/latest"),
    staleTime: 10 * 60_000,
  });
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const d = q.data;
  const r = d?.latest ?? null;
  const months = r ? r.months.split(",") : [];
  const period = months.length ? months.map(monthLabel).join(" and ") : "";
  const stale = r ? Date.now() - r.run_at > STALE_MS : false;

  let tone: "ok" | "warn" | "muted" = "muted";
  let label = "NetSuite check: not run yet";
  if (q.error) label = "NetSuite check unavailable";
  else if (r?.status === "ok") {
    tone = stale ? "warn" : "ok";
    label = `✓ Matches NetSuite · checked ${ago(r.run_at)}`;
  } else if (r?.status === "mismatch") {
    tone = "warn";
    label = `${r.mismatched} SKU-month${r.mismatched === 1 ? "" : "s"} differ from NetSuite · checked ${ago(r.run_at)}`;
  } else if (r?.status === "failed") {
    tone = "warn";
    label = `NetSuite check failed ${ago(r.run_at)}`;
  }

  const palette = {
    ok: { bg: theme.color.successFaint, fg: theme.color.successText, border: theme.color.successBorder },
    warn: { bg: theme.color.warningFaint, fg: theme.color.warningText, border: theme.color.warningBorder },
    muted: { bg: theme.color.surfaceHover, fg: theme.color.textMuted, border: theme.color.border },
  }[tone];

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        disabled={q.isLoading}
        style={{
          padding: "4px 10px",
          borderRadius: 999,
          border: `1px solid ${palette.border}`,
          background: palette.bg,
          color: palette.fg,
          fontSize: theme.font.size.xs,
          fontWeight: theme.font.weight.semibold,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {q.isLoading ? "Checking NetSuite tie-out…" : label}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="NetSuite tie-out"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 6px)",
            zIndex: 40,
            width: "min(440px, calc(100vw - 32px))",
            maxHeight: 460,
            overflowY: "auto",
            background: theme.color.surface,
            border: `1px solid ${theme.color.border}`,
            borderRadius: theme.radius.lg,
            boxShadow: theme.shadow.lg,
            padding: theme.space.lg,
            fontSize: theme.font.size.sm,
            color: theme.color.text,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Nightly check against NetSuite</div>
          <div style={{ color: theme.color.textMuted, lineHeight: 1.5 }}>
            Every night the app asks NetSuite how many units of each SKU sold on revenue lines,
            the same figure as the <b>Sales by Item</b> report for March Products locations, and
            compares it with the "sold alone" numbers shown here.
          </div>

          {!r && !q.error && (
            <div style={{ marginTop: 12, color: theme.color.textMuted }}>
              The first check runs tonight.
            </div>
          )}

          {r && r.status !== "failed" && (
            <div style={{ marginTop: 12, display: "grid", gap: 4 }}>
              <div>
                <b>{period}</b> · {fmtNum(r.skus_checked)} SKUs with sales
              </div>
              <div style={{ color: theme.color.textMuted }}>
                App {fmtNum(Math.round(r.units_app))} units · NetSuite{" "}
                {fmtNum(Math.round(r.units_ns))} units
              </div>
              <div style={{ color: theme.color.textFaint, fontSize: theme.font.size.xs }}>
                Checked {new Date(r.run_at).toLocaleString()}
                {stale && " — older than a day, so the last nightly check did not run"}
              </div>
            </div>
          )}

          {r?.status === "failed" && (
            <div style={{ marginTop: 12, color: theme.color.warningText }}>
              The check could not reach NetSuite: {r.error_message ?? "unknown error"}.
              {d?.last_ok_at
                ? ` The last full match was ${new Date(d.last_ok_at).toLocaleDateString()}.`
                : ""}
            </div>
          )}

          {r?.status === "ok" && (
            <div
              style={{
                marginTop: 12,
                padding: "8px 10px",
                borderRadius: theme.radius.md,
                background: theme.color.successFaint,
                color: theme.color.successText,
              }}
            >
              Every SKU matches NetSuite to the unit.
            </div>
          )}

          {r?.status === "mismatch" && d && (
            <>
              <div style={{ marginTop: 12, color: theme.color.textMuted, lineHeight: 1.5 }}>
                These differ. Last month heals overnight when an invoice was posted late or
                backdated. A difference that stays for several nights needs looking at.
              </div>
              <table
                style={{
                  marginTop: 8,
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: theme.font.size.xs,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                <thead>
                  <tr style={{ color: theme.color.textMuted, textAlign: "right" }}>
                    <th style={{ textAlign: "left", padding: "4px 0" }}>SKU</th>
                    <th>Month</th>
                    <th>App</th>
                    <th>NetSuite</th>
                    <th>Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {d.diffs.map((x) => (
                    <tr
                      key={`${x.sku_itemid}|${x.ym}`}
                      style={{ borderTop: `1px solid ${theme.color.border}`, textAlign: "right" }}
                    >
                      <td style={{ textAlign: "left", padding: "4px 0", fontFamily: theme.font.mono }}>
                        {x.sku_itemid}
                      </td>
                      <td>{monthLabel(x.ym)}</td>
                      <td>{fmtNum(x.app_qty)}</td>
                      <td>{fmtNum(x.ns_qty)}</td>
                      <td>
                        {x.app_qty - x.ns_qty > 0 ? "+" : ""}
                        {fmtNum(x.app_qty - x.ns_qty)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {r.mismatched > d.diffs.length && (
                <div style={{ marginTop: 6, color: theme.color.textFaint, fontSize: theme.font.size.xs }}>
                  Showing the {d.diffs.length} largest of {fmtNum(r.mismatched)}.
                </div>
              )}
            </>
          )}

          <div style={{ marginTop: 12, color: theme.color.textFaint, fontSize: theme.font.size.xs }}>
            Units used inside umbrellas have no NetSuite report to check against. Click any
            fiscal-year figure in the Plan view to see how those add up.
          </div>
        </div>
      )}
    </div>
  );
}
